import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { getSignerTitle } from "@/pages/esign/esign.config.ts";
import { useEsignReminder } from "@/pages/esign/use-esign-reminder.ts";
import { EsignDocumentPreviewModal } from "@/pages/esign/widgets/EsignDocumentPreviewModal.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import {
  useDocument,
  useDocumentFoldersList,
  useDocumentsSearch,
} from "@/queries/use-documents.query.ts";
import { useEsignMarkup } from "@/queries/use-esign.query.ts";
import { useWorkflowUpdateBlockActionEsign } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiDocument, ApiFolderType } from "@/types/document.types.ts";
import { EsignSignatureType } from "@/types/esign.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiWorkflowBlockActionEsign } from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Avatar,
  Box,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  Input,
  ListItem,
  Modal,
  ModalProps,
  Radio,
  Select,
  SelectItem,
  SelectValueType,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "./WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionEsignModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionEsign: ApiWorkflowBlockActionEsign | null;
}

interface EsignFormValues extends WorkflowActionTimingFormFields {
  block_title: string | null;
  due_date_delay_days: number | null;
  document_id: number | null;
  target_folder_id: number | null;
  signature_type: EsignSignatureType;
}

export function WorkflowEditActionEsignModal({
  workflowVersion,
  block,
  actionEsign,
  ...props
}: WorkflowEditActionEsignModalProps) {
  const { close, openModal } = useModals();
  const { id } = props;
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const { showError } = useToasts();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const canManageEsignTemplates =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_manage_templates);

  const reminder = useEsignReminder({
    reminder_mode: actionEsign?.reminder_mode ?? null,
    reminder_custom_frequency: actionEsign?.reminder_custom_frequency ?? null,
    reminder_custom_period: actionEsign?.reminder_custom_period ?? null,
  });

  const [activeTab, setActiveTab] = useState(0);
  const [documentSearch, setDocumentSearch] = useState<string | undefined>(undefined);

  const schemaObject = yup.object({
    block_title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    due_date_delay_days: yup
      .number()
      .nullable()
      .defined()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue == null || Number.isNaN(value)) {
          return null;
        }

        return value;
      })
      .typeError(t("errors:field_number_required"))
      .min(0, t("api_errors:minimum_value_must", { minimum: 0 })),
    document_id: yup
      .number()
      .nullable()
      .defined()
      .test("required", t("errors:field_required"), (value) => value != null),
    target_folder_id: yup.number().nullable().defined(),
    signature_type: yup
      .mixed<EsignSignatureType>()
      .oneOf(Object.values(EsignSignatureType))
      .required(),
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<EsignFormValues>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      block_title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      due_date_delay_days: actionEsign?.due_date_delay_days ?? null,
      document_id: actionEsign?.document_id ?? null,
      target_folder_id: actionEsign?.target_folder_id ?? null,
      signature_type: actionEsign?.signature_type ?? EsignSignatureType.acknowledge,
    },
  });

  const documentId = useWatch({ control, name: "document_id" });
  const targetFolderId = useWatch({ control, name: "target_folder_id" });
  const signatureType = useWatch({ control, name: "signature_type" });

  const { data: folders } = useDocumentFoldersList(ApiFolderType.employee);
  const { data: documentsData, isLoading: isDocumentsLoading } = useDocumentsSearch({
    folder_type: ApiFolderType.organization,
    title: documentSearch,
  });
  const { data: selectedDocument } = useDocument(documentId ?? 0, documentId != null);
  const canSign = selectedDocument?.can_sign ?? false;
  const { data: markup } = useEsignMarkup(
    documentId ?? 0,
    documentId != null && signatureType === EsignSignatureType.signature
  );

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const documentCaption = (document: Pick<ApiDocument, "can_sign" | "can_acknowledge">): string => {
    if (document.can_sign) return t("core:ready_for_signing");
    if (document.can_acknowledge) return t("workflow:action_type_esign_acknowledgment_only");
    return t("workflow:action_type_esign_not_supported");
  };

  const documentOptions: SelectItem[] = (documentsData?.items ?? []).map((item) => ({
    id: item.id,
    title: item.title,
    caption: documentCaption(item),
  }));

  const handleDocumentChange = (value: SelectValueType) => {
    if (value == null) {
      setValue("document_id", null, { shouldValidate: true });
      return;
    }
    const document = documentsData?.items.find((item) => item.id === Number(value));
    if (document && !document.can_sign && !document.can_acknowledge) {
      showError({ title: t("workflow:action_type_esign_not_supported") });
      return;
    }
    setValue("document_id", Number(value), { shouldValidate: true });
  };

  useEffect(() => {
    if (selectedDocument && !canSign && signatureType === EsignSignatureType.signature) {
      setValue("signature_type", EsignSignatureType.acknowledge);
    }
  }, [selectedDocument, canSign, signatureType, setValue]);

  const folderOptions: SelectItem[] = (folders ?? []).map((item) => ({
    id: item.id,
    title: item.title,
  }));
  const selectedFolder = folders?.find((item) => item.id === targetFolderId);

  useEffect(() => {
    if (targetFolderId != null || !folders?.length) return;
    setValue("target_folder_id", folders[0].id);
  }, [folders, targetFolderId, setValue]);

  const updateBlockAction = useWorkflowUpdateBlockActionEsign(control);

  const handleSave = handleSubmit(
    async (data) => {
      try {
        const reminderPayload = reminder.buildPayload();

        await updateBlockAction.mutateAsync({
          workflowId: workflowVersion.workflow_id,
          workflowVersionId: workflowVersion.id,
          workflowBlockId: block.id,
          payload: {
            title: data.block_title,
            ...getWorkflowActionTimingPayload(data),
            block_data: {
              document_id: data.document_id,
              target_folder_id: data.target_folder_id,
              signature_type: data.signature_type,
              use_due_date: data.due_date_delay_days != null,
              due_date_delay_days: data.due_date_delay_days,
              reminder_mode: reminderPayload.reminder_mode,
              reminder_custom_frequency: reminderPayload.reminder_custom_frequency ?? null,
              reminder_custom_period: reminderPayload.reminder_custom_period ?? null,
            },
            conditions: conditions,
          },
        });

        analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
          [analyticProperties.id]: workflowVersion.workflow_id,
          [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.esign,
        });

        close(id);
      } catch {}
    },
    (formErrors) => {
      if (formErrors.document_id) setActiveTab(0);
    }
  );

  return (
    <Modal
      {...props}
      variant={"side"}
      title={t("workflow:action_type_esign")}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      size={"lg"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            onClick={handleSave}
            isLoading={updateBlockAction.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
          <TabItem title={t("common:settings")}>
            <Stack className={"py-6"} gap={"lg"}>
              <Stack gap={"xs"}>
                <Text className={"title-sub-headline"}>{t("core:document")}</Text>
                <Select
                  emptyTitle={t("core:document")}
                  required={true}
                  error={errors.document_id?.message}
                  searchable={true}
                  showCaption={true}
                  loadOptionsOnOpen={true}
                  isLoading={isDocumentsLoading}
                  options={
                    selectedDocument
                      ? [
                          {
                            id: selectedDocument.id,
                            title: selectedDocument.title,
                            caption: documentCaption(selectedDocument),
                          },
                        ]
                      : undefined
                  }
                  onSearchOptions={setDocumentSearch}
                  searchOptions={documentOptions}
                  value={documentId}
                  onChange={handleDocumentChange}
                />
                {selectedDocument && (
                  <ListItem
                    behavior={"card"}
                    className={"py-3"}
                    icon={
                      <Avatar
                        size={"md"}
                        icon={<FontAwesomeIcon icon={"fa-light fa-file-contract"} />}
                      />
                    }
                    title={selectedDocument.title}
                    caption={selectedDocument.file?.name}
                    valueSlot={
                      <Button
                        variant={"secondary"}
                        size={"sm"}
                        className={"shrink-0 whitespace-nowrap"}
                        disabled={!selectedDocument.file}
                        onClick={() => {
                          if (!selectedDocument.file) return;
                          openModal(EsignDocumentPreviewModal, {
                            documentId: selectedDocument.id,
                            file: selectedDocument.file,
                            fileName: selectedDocument.file.name,
                            preview: selectedDocument.preview,
                            showFields: signatureType === EsignSignatureType.signature,
                          });
                        }}
                      >
                        {t("common:preview")}
                      </Button>
                    }
                  />
                )}
              </Stack>

              {selectedDocument && (
                <Stack gap={"sm"}>
                  <Text className={"title-sub-headline"}>
                    {t("workflow:action_type_esign_request_type")}
                  </Text>
                  <Box variant={"border"} className={"px-4 py-2"}>
                    <Radio
                      value={EsignSignatureType.acknowledge}
                      checked={signatureType === EsignSignatureType.acknowledge}
                      onChange={() => setValue("signature_type", EsignSignatureType.acknowledge)}
                      title={t("workflow:action_type_esign_acknowledgment")}
                      caption={t("workflow:action_type_esign_acknowledgment_hint")}
                    />
                    <Radio
                      value={EsignSignatureType.signature}
                      checked={signatureType === EsignSignatureType.signature}
                      disabled={!canSign}
                      onChange={() => setValue("signature_type", EsignSignatureType.signature)}
                      title={t("workflow:action_type_esign")}
                      caption={
                        canSign
                          ? t("workflow:action_type_esign_signatures_hint")
                          : t("workflow:action_type_esign_not_prepared")
                      }
                    />
                    {!canSign && canManageEsignTemplates && (
                      <Box className={"pl-8 pt-2 pb-2"}>
                        <ButtonLink
                          variant={"secondary"}
                          size={"sm"}
                          href={`/docs/documents/${documentId}/markup`}
                          blank={true}
                          rightIcon={
                            <FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />
                          }
                        >
                          {t("workflow:action_type_esign_prepare")}
                        </ButtonLink>
                      </Box>
                    )}
                  </Box>
                </Stack>
              )}

              {selectedDocument && signatureType === EsignSignatureType.signature && (
                <Stack gap={"xs"}>
                  <Text className={"title-sub-headline"}>{t("core:esign_signers")}</Text>
                  <Stack>
                    {markup?.roles.map((role, index) => {
                      const title = getSignerTitle(role, t);
                      return (
                        <Stack
                          key={`signer-${role.id ?? index}`}
                          direction={"horizontal"}
                          items={"center"}
                          gap={"md"}
                          className={"border-b border-dark/5 py-2"}
                        >
                          <Avatar acronym={title} />
                          <Text className={"text-title"}>{title}</Text>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              )}

              <Stack gap={"xs"}>
                <Text className={"title-sub-headline"}>{t("core:esign_reminders")}</Text>
                <Stack direction={"horizontal"} gap={"sm"}>
                  <Box className={"flex-1"}>
                    <Input
                      type="number"
                      min={0}
                      label={t("workflow:action_type_task_due_date_delay_days")}
                      {...register("due_date_delay_days", {
                        setValueAs: (value) =>
                          value === "" || value == null ? null : Number(value),
                      })}
                      error={errors.due_date_delay_days?.message}
                    />
                  </Box>
                  <Box className={"flex-1"}>
                    <Select
                      emptyTitle={t("core:esign_reminder_frequency")}
                      showCaption={true}
                      captionPerItem={true}
                      options={reminder.options}
                      value={reminder.value}
                      onChange={reminder.onChange}
                    />
                  </Box>
                </Stack>
              </Stack>

              <Stack gap={"xs"}>
                <Text className={"title-sub-headline"}>
                  {t("core:esign_store_signed_document")}
                </Text>
                <Select
                  label={t("core:folder")}
                  emptyTitle={t("core:folder")}
                  options={folderOptions}
                  value={targetFolderId}
                  onChange={(value) =>
                    setValue("target_folder_id", value != null ? Number(value) : null)
                  }
                />
                {selectedFolder && (
                  <Text className={"text-secondary"} size={"xs"}>
                    {`${t("core:esign_store_path_profile")} → ${t("core:esign_store_path_docs")} → ${selectedFolder.title}`}
                  </Text>
                )}
              </Stack>
            </Stack>
          </TabItem>
          <TabItem title={t("workflow:conditions")}>
            <Stack gap={"lg"} className={"py-6"}>
              <Stack>
                <Title header={t("workflow:block")} size={"xs"} />
                <Input
                  label={t("common:title")}
                  type="text"
                  {...register("block_title")}
                  error={errors.block_title?.message}
                />
              </Stack>
              <WorkflowActionTimingEditor
                control={control}
                register={register}
                errors={errors}
                triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
              />

              <WorkflowBlockConditionsList
                conditions={conditions}
                onAdd={handleAddCondition}
                onEdit={handleEditCondition}
                onDelete={handleDeleteCondition}
              />
            </Stack>
          </TabItem>
        </Tabs>
      </form>
    </Modal>
  );
}
